import {
  useQueries,
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import {useMemo} from 'react';

import {
  fetchKnowledgeNavMenu,
  fetchServiceNowKnowledgePage,
  fetchServiceNowKnowledgePages,
  ServiceNowKnowledgePageNode,
  ServiceNowKnowledgePagesResponse,
  ServiceNowNavMenuResponse,
} from './api';
import {shouldFetchKnowledgePageDetail} from './knowledgePageSelection';
import {loadNavMenuSnapshot, saveNavMenuSnapshot} from './navMenuSnapshot';

const KNOWLEDGE_STALE_TIME_MS = 60 * 60 * 1000;
// sync.worker's refresh_servicenow_content runs at 03:00 UTC; give it an hour to land.
const KNOWLEDGE_SYNC_LANDED_UTC_HOUR = 4;

/**
 * Time from `updatedAt` until the first nightly sync after it has landed.
 * Topics only change then; who can see restricted ones may lag up to a day,
 * which is fine since opening a page checks access again.
 */
export const msUntilNextKnowledgeSync = (updatedAt: number) => {
  const next = new Date(updatedAt);
  next.setUTCHours(KNOWLEDGE_SYNC_LANDED_UTC_HOUR, 0, 0, 0);
  if (next.getTime() <= updatedAt) {
    next.setUTCDate(next.getUTCDate() + 1);
  }
  return next.getTime() - updatedAt;
};

type UserScopedKnowledgeQueryOptions<T> = Pick<UseQueryOptions<T>, 'enabled'> & {
  cacheScope?: string | null;
};

export const useKnowledgeNavMenu = (
  options?: UserScopedKnowledgeQueryOptions<ServiceNowNavMenuResponse>
) => {
  const {cacheScope = null, ...queryOptions} = options ?? {};
  return useQuery({
    queryKey: ['knowledge', 'nav-menu', cacheScope],
    queryFn: async () => {
      const menu = await fetchKnowledgeNavMenu();
      saveNavMenuSnapshot(cacheScope, menu);
      return menu;
    },
    // Last load's menu paints at once; after the next sync it refetches behind it.
    initialData: () => loadNavMenuSnapshot(cacheScope)?.menu,
    initialDataUpdatedAt: () => loadNavMenuSnapshot(cacheScope)?.savedAt,
    staleTime: (query) => msUntilNextKnowledgeSync(query.state.dataUpdatedAt),
    ...queryOptions,
  });
};

const knowledgePagesQueryKey = (cacheScope: string | null, categorySlug: string | null) => [
  'knowledge',
  'servicenow',
  'pages',
  cacheScope,
  categorySlug,
];

export const useServiceNowKnowledgePages = (
  categorySlug?: string | null,
  options?: UserScopedKnowledgeQueryOptions<ServiceNowKnowledgePagesResponse>
) => {
  const normalizedCategorySlug = categorySlug?.trim() || null;
  const {cacheScope = null, ...queryOptions} = options ?? {};

  return useQuery({
    queryKey: knowledgePagesQueryKey(cacheScope, normalizedCategorySlug),
    queryFn: () => fetchServiceNowKnowledgePages(normalizedCategorySlug),
    staleTime: KNOWLEDGE_STALE_TIME_MS,
    ...queryOptions,
  });
};

const combineCategoryPages = (results: UseQueryResult<ServiceNowKnowledgePagesResponse>[]) => ({
  pagesByCategory: results.map((result) => result.data?.pages ?? null),
  isLoading: results.some((result) => result.isLoading),
});

/** Every category's pages, sharing the cache with each category's own hub. */
export const useAllKnowledgeCategoryPages = (
  categorySlugs: string[],
  {enabled, cacheScope = null}: {enabled: boolean; cacheScope?: string | null}
) =>
  useQueries({
    queries: categorySlugs.map((categorySlug) => ({
      queryKey: knowledgePagesQueryKey(cacheScope, categorySlug),
      queryFn: () => fetchServiceNowKnowledgePages(categorySlug),
      staleTime: KNOWLEDGE_STALE_TIME_MS,
      enabled,
    })),
    combine: combineCategoryPages,
  });

export const useServiceNowKnowledgePage = (
  id?: string | null,
  options?: UserScopedKnowledgeQueryOptions<ServiceNowKnowledgePageNode>
) => {
  const normalizedId = id?.trim() || null;
  const {cacheScope = null, enabled = true} = options ?? {};

  return useQuery({
    queryKey: ['knowledge', 'servicenow', 'page', cacheScope, normalizedId],
    queryFn: () => {
      if (!normalizedId) {
        throw new Error('Knowledge page id is required');
      }
      return fetchServiceNowKnowledgePage(normalizedId);
    },
    enabled: Boolean(normalizedId) && enabled,
    staleTime: KNOWLEDGE_STALE_TIME_MS,
  });
};

/** Keep article reads independent of category discovery and its access checks. */
export const useKnowledgeContent = ({
  selectedPageId,
  categorySlug,
  isNavMenuLoading,
  cacheScope,
}: {
  selectedPageId: string | null;
  categorySlug: string | null;
  isNavMenuLoading: boolean;
  cacheScope?: string | null;
}) => {
  const isArticleRoute = Boolean(selectedPageId?.startsWith('article-'));
  const categoryPagesQuery = useServiceNowKnowledgePages(categorySlug, {
    enabled: Boolean(categorySlug) && !isArticleRoute,
    cacheScope,
  });
  const treePages = useMemo(
    () => (isArticleRoute ? [] : (categoryPagesQuery.data?.pages ?? [])),
    [isArticleRoute, categoryPagesQuery.data?.pages]
  );
  const nodeById = useMemo(() => {
    const nodesById = new Map<string, ServiceNowKnowledgePageNode>();
    const visit = (nodes: ServiceNowKnowledgePageNode[]) => {
      for (const node of nodes) {
        nodesById.set(node.id, node);
        visit(node.children);
      }
    };
    visit(treePages);
    return nodesById;
  }, [treePages]);
  const selectedPageFromTree = selectedPageId ? (nodeById.get(selectedPageId) ?? null) : null;
  const shouldFetchDetail = shouldFetchKnowledgePageDetail({
    selectedPageId,
    selectedPageFromTree,
    isCategoryPagesLoading: categoryPagesQuery.isLoading,
    isNavMenuLoading,
  });
  const pageQuery = useServiceNowKnowledgePage(selectedPageId, {
    enabled: shouldFetchDetail,
    cacheScope,
  });
  const selectedPage = pageQuery.data ?? (shouldFetchDetail ? null : selectedPageFromTree) ?? null;
  const isLoading = isArticleRoute
    ? pageQuery.isLoading || pageQuery.isFetching
    : isNavMenuLoading ||
      categoryPagesQuery.isLoading ||
      (shouldFetchDetail && (pageQuery.isLoading || pageQuery.isFetching));
  const isPageNotFound =
    Boolean(selectedPageId) &&
    !isLoading &&
    !selectedPage &&
    (isArticleRoute ? pageQuery.isFetched : categoryPagesQuery.isFetched || pageQuery.isFetched);
  return {treePages, selectedPage, isLoading, isPageNotFound};
};
